import React, { useState, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';

const SplitPDF = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [splitting, setSplitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]?.type === 'application/pdf') {
      const file = files[0];
      setSelectedFile(file);
      
      // Get page count
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      setPageCount(pdf.getPageCount());
      setSelectedPages([]);

      // Create preview URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      const previewUrl = URL.createObjectURL(file);
      setPdfPreviewUrl(previewUrl);
    }
  };

  const togglePageSelection = (pageNum: number) => {
    setSelectedPages(pages => {
      if (pages.includes(pageNum)) {
        return pages.filter(p => p !== pageNum);
      } else {
        return [...pages, pageNum].sort((a, b) => a - b);
      }
    });
  };

  const splitPDF = async () => {
    if (!selectedFile || selectedPages.length === 0) return;

    setSplitting(true);
    setProgress(0);

    try {
      // Load the PDF
      const fileArrayBuffer = await selectedFile.arrayBuffer();
      const sourcePdf = await PDFDocument.load(fileArrayBuffer);
      
      // Create new PDF with selected pages
      const newPdf = await PDFDocument.create();
      const totalPages = selectedPages.length;
      let processedPages = 0;

      for (const pageNum of selectedPages) {
        const [copiedPage] = await newPdf.copyPages(sourcePdf, [pageNum - 1]);
        newPdf.addPage(copiedPage);
        processedPages++;
        setProgress((processedPages / totalPages) * 100);
      }

      // Save the new PDF
      const newPdfBytes = await newPdf.save();
      const newPdfBlob = new Blob([newPdfBytes], { type: 'application/pdf' });
      
      // Trigger download
      const downloadUrl = URL.createObjectURL(newPdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'split-document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error splitting PDF:', error);
    } finally {
      setSplitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
        <input
          type="file"
          accept=".pdf"
          onChange={handleFileSelect}
          className="hidden"
          id="pdf-upload"
        />
        <label
          htmlFor="pdf-upload"
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/SplitPdf.svg`}
            alt="Upload PDF"
            className="w-12 h-12 mb-3"
          />
          <span className="text-lg font-medium mb-1">
            {selectedFile ? selectedFile.name : 'Choose a PDF file'}
          </span>
          <span className="text-sm text-gray-500">
            {selectedFile ? `${pageCount} pages` : 'or drag and drop here'}
          </span>
        </label>
      </div>

      {selectedFile && pageCount > 0 && (
        <>
          <div className="p-4 bg-white rounded-lg shadow">
            <h3 className="text-lg font-medium mb-3">Select pages to extract</h3>
            <div className="grid grid-cols-6 gap-2">
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => togglePageSelection(pageNum)}
                  className={`p-2 rounded ${
                    selectedPages.includes(pageNum)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {selectedPages.length} pages selected
            </span>
            <button
              onClick={splitPDF}
              disabled={splitting || selectedPages.length === 0}
              className={`px-6 py-2 rounded-lg text-white ${
                splitting || selectedPages.length === 0
                  ? 'bg-gray-400'
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {splitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Splitting... {Math.round(progress)}%
                </div>
              ) : (
                'Split PDF'
              )}
            </button>
          </div>
        </>
      )}

      {pdfPreviewUrl && (
        <div className="mt-4">
          <iframe
            src={pdfPreviewUrl}
            className="w-full h-[600px] border border-gray-300 rounded"
            title="PDF Preview"
          />
        </div>
      )}
    </div>
  );
};

export default SplitPDF;
